'use client';

import { c as _c } from "react/compiler-runtime";
import type { AnswerState } from '@repo/contracts/cardfinder/funnel/types';
import { useClientABSmartlyExperiments } from '@repo/platform-absmartly/hooks/useClientABSmartlyExperiments';
import { ABTestVariant } from '@repo/platform-absmartly/utils/variants';
import { usePageAction } from '@repo/platform-analytics/hooks/usePageAction';
import { useRouter } from 'next/navigation';
import { CARDFINDER_THINKING_INTERSTITIAL_ID } from '@/features/experiments/constants';
import type { BranchId } from '../../config/constants';
import { useCompleteFunnelRun } from '../answer/session/useCompleteFunnelRun';
import { useFunnelConfigVersion, useFunnelFlattenedConfig } from './Context/hooks';
import { FUNNEL_ENTRY_PARAM, FUNNEL_ENTRY_VALUE, RECOMMENDATIONS_PATH } from './constants';
import { withCurrentSearchParams } from './navigationUtils';

/**
 * The end of a funnel run: commit it, then show the results.
 *
 * This is the funnel's completion signal — reached only from `resumeAfterStep`
 * when the current step ends the branch.
 *
 * `submittedAnswers` must be the answer set *including* the final step's answer.
 * The caller has it; the caller's store snapshot predates the final
 * `SUBMIT_ANSWER` dispatch, so it can't be read back from there.
 */
export function useFinishFunnel() {
  const $ = _c(7);
  const router = useRouter();
  const funnelFlattenedConfig = useFunnelFlattenedConfig();
  const funnelConfigVersion = useFunnelConfigVersion();
  const completeRun = useCompleteFunnelRun(funnelFlattenedConfig, funnelConfigVersion);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      actionName: "cc_recs_submit",
      actionSection: "cardfinder_flow",
      actionType: "submit"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const firePageAction = usePageAction(t0);
  const {
    results,
    isReady
  } = useClientABSmartlyExperiments(CARDFINDER_THINKING_INTERSTITIAL_ID);
  const variant = results[0];
  let t1;
  if ($[1] !== completeRun || $[2] !== firePageAction || $[3] !== isReady || $[4] !== router || $[5] !== variant) {
    t1 = async (branchId, submittedAnswers) => {
      firePageAction({
        additional_attributes: {
          branch_id: branchId
        }
      });
      const result = await completeRun(submittedAnswers);
      if (result === "version-mismatch") {
        return;
      }
      if (isReady && variant === ABTestVariant.Treatment) {
        router.push(withCurrentSearchParams(`${RECOMMENDATIONS_PATH}?${FUNNEL_ENTRY_PARAM}=${FUNNEL_ENTRY_VALUE}`));
      } else {
        router.push(withCurrentSearchParams(`${RECOMMENDATIONS_PATH}`));
      }
    };
    $[1] = completeRun;
    $[2] = firePageAction;
    $[3] = isReady;
    $[4] = router;
    $[5] = variant;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}